<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import LiquidityChart from './LiquidityChart.vue'
import { useFormat } from '../composables/useFormat.js'
import { useLocalization } from '@/composables/useLocalization'

const ALL_TIMEFRAMES = ['1m', '3m', '5m', '15m', '30m', '1h', '2h', '4h', '1d']

type PointRow = {
  id: number
  side: string
  side_label: string
  price: number
  status: string
  status_label: string
  mtf_label?: string | null
  url: string
}

const props = defineProps<{
  open: boolean
  exchange: string
  label: string
  symbol: string
  lastPrice?: number | null
  zones: any
  timeframe: string
  apiBase: string
  refreshMs: number
  longs: PointRow[]
  shorts: PointRow[]
}>()

const emit = defineEmits<{
  close: []
  block: [payload: { timeframe: string; side: 'long' | 'short'; price: number }]
  points: []
}>()

const { __ } = useLocalization()
const { price } = useFormat()

const localTf = ref(props.timeframe)
const viewport = ref({ width: 1280, height: 800 })

const chartKey = computed(() => `${props.symbol}:${localTf.value}`)
const pointCount = computed(() => props.longs.length + props.shorts.length)

const chartHeight = computed(() => {
  const chrome = viewport.value.width < 720 ? 124 : 108
  return Math.max(280, viewport.value.height - chrome)
})

function measure() {
  viewport.value = {
    width: window.innerWidth,
    height: window.innerHeight,
  }
}

function onKey(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.open) {
    event.preventDefault()
    emit('close')
  }
}

watch(
  () => [props.open, props.timeframe, props.symbol],
  () => {
    if (props.open) localTf.value = props.timeframe
  },
)

watch(
  () => props.open,
  (open) => {
    document.body.classList.toggle('board-chart-open', open)
    if (open) {
      measure()
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')))
    }
  },
)

onMounted(() => {
  measure()
  window.addEventListener('resize', measure, { passive: true })
  window.addEventListener('keydown', onKey)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', measure)
  window.removeEventListener('keydown', onKey)
  document.body.classList.remove('board-chart-open')
})
</script>

<template>
  <Teleport to="body">
    <Transition name="board-chart">
      <div
        v-if="open"
        class="board-chart"
        role="dialog"
        aria-modal="true"
        :aria-label="__('Expand chart')"
        @click.self="emit('close')"
      >
        <div class="board-chart__card" dir="ltr" @click.stop>
          <header class="board-chart__head">
            <div class="board-chart__id min-w-0">
              <p class="board-chart__kicker">{{ __('Expand chart') }}</p>
              <h3 class="board-chart__title">
                {{ label }}
                <span class="board-chart__sym">{{ symbol }}</span>
              </h3>
              <p v-if="lastPrice != null" class="board-chart__live">
                <span class="board-chart__live-dot" />
                {{ __('Live') }}
                <span class="board-chart__live-px">{{ price(lastPrice) }}</span>
              </p>
            </div>

            <div class="board-chart__tfs" role="tablist" :aria-label="__('Timeframe')">
              <button
                v-for="tf in ALL_TIMEFRAMES"
                :key="tf"
                type="button"
                class="board-chart__tf"
                :class="{ 'is-active': tf === localTf }"
                @click="localTf = tf"
              >
                {{ tf }}
              </button>
            </div>

            <div class="board-chart__end">
              <button
                type="button"
                class="board-chart__points"
                @click="emit('points')"
              >
                {{ __('Points') }}
                <span v-if="pointCount" class="board-chart__count">{{ pointCount }}</span>
              </button>
              <button
                type="button"
                class="board-chart__close"
                :aria-label="__('Close')"
                @click="emit('close')"
              >
                ×
              </button>
            </div>
          </header>

          <div class="board-chart__body">
            <LiquidityChart
              :key="chartKey"
              :exchange="exchange"
              :symbol="symbol"
              :timeframe="localTf"
              :levels="zones"
              :api-base="apiBase"
              :height="chartHeight"
              :refresh-ms="refreshMs"
              :candle-limit="1000"
              @block="emit('block', $event)"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.board-chart {
  position: fixed;
  inset: 0;
  z-index: 88;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  background: rgba(8, 10, 16, 0.82);
  backdrop-filter: blur(16px);
}
.board-chart__card {
  display: flex;
  flex-direction: column;
  width: min(96rem, 100%);
  height: calc(100vh - 1.5rem);
  overflow: hidden;
  border-radius: 1.1rem;
  background: #131722;
  border: 1px solid #363a45;
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.62);
}
.board-chart__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem 0.9rem;
  padding: 0.7rem 0.9rem;
  border-bottom: 1px solid #2a2e39;
  background: #1e222d;
  flex-shrink: 0;
}
.board-chart__kicker {
  margin: 0;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #8b919d;
}
.board-chart__title {
  margin: 0.15rem 0 0;
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #f0f3fa;
  line-height: 1.15;
}
.board-chart__sym {
  margin-inline-start: 0.4rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.75rem;
  font-weight: 600;
  color: #8b919d;
}
.board-chart__live {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0.3rem 0 0;
  font-size: 12px;
  font-weight: 600;
  color: #8b919d;
}
.board-chart__live-dot {
  width: 0.42rem;
  height: 0.42rem;
  border-radius: 999px;
  background: #26a69a;
  box-shadow: 0 0 0 4px rgba(38, 166, 154, 0.16);
}
.board-chart__live-px {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: #f0f3fa;
}
.board-chart__tfs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  flex: 1 1 14rem;
}
.board-chart__tf {
  border-radius: 999px;
  border: 1px solid #363a45;
  background: #131722;
  color: #d1d4dc;
  padding: 0.28rem 0.62rem;
  font-size: 11px;
  font-weight: 700;
}
.board-chart__tf.is-active {
  background: #2962ff;
  border-color: #2962ff;
  color: #fff;
}
.board-chart__end {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-inline-start: auto;
}
.board-chart__points {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border-radius: 999px;
  border: 1px solid rgba(59, 130, 246, 0.35);
  background: rgba(59, 130, 246, 0.12);
  color: #60a5fa;
  padding: 0.32rem 0.7rem;
  font-size: 12px;
  font-weight: 700;
}
.board-chart__count {
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.22);
  padding: 0 0.35rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.board-chart__close {
  width: 2.15rem;
  height: 2.15rem;
  border-radius: 0.7rem;
  border: 1px solid #3d4250;
  background: #131722;
  color: #d1d4dc;
  font-size: 1.35rem;
  line-height: 1;
}
.board-chart__close:hover {
  background: #2a2e39;
  color: #fff;
}
.board-chart__body {
  flex: 1;
  min-height: 0;
  padding: 0.45rem;
}
.board-chart-enter-active,
.board-chart-leave-active {
  transition: opacity 0.18s ease;
}
.board-chart-enter-from,
.board-chart-leave-to {
  opacity: 0;
}
@media (max-width: 720px) {
  .board-chart {
    padding: 0;
  }
  .board-chart__card {
    width: 100%;
    height: 100vh;
    height: 100dvh;
    border-radius: 0;
  }
}
</style>

<style>
body.board-chart-open {
  overflow: hidden;
}
</style>
